import React, { createContext, useContext, useState, useRef } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import { Box, Button, CircularProgress, Divider, Typography } from "@mui/joy";

interface ModalContextType {
  openModal: (
    content: React.ReactElement,
    options?: {
      title?: string;
      onSubmit?: () => void | Promise<void>;
      onCancel?: () => void | Promise<void>;
      saving?: boolean;
    }
  ) => void;
  closeModal: () => void;
  setSaving: (saving: boolean) => void;
  saving: boolean;
  registerSubmitCallback: (callback: () => Promise<void>) => void;
  canSubmit: boolean;
  setModalTitle: (title: string | null) => void;
  setCanSubmit: (canSubmit: boolean) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalTitle, setModalTitle] = useState<string | null>(null);
  const [modalContent, setModalContent] = useState<React.ReactElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true); // Initialize to true or false as needed
  const [onSubmitCallback, setOnSubmitCallback] = useState<(() => void | Promise<void>) | null>(null);
  const [onCancelCallback, setOnCancelCallback] = useState<(() => void | Promise<void>) | null>(null);

  // Store the callback function reference using useRef
  const handleSubmitCallbackRef = useRef<(() => Promise<void>) | null>(null);

  const openModal = (
    content: React.ReactElement,
    options?: { title?: string; onSubmit?: () => void | Promise<void>; onCancel?: () => void | Promise<void>; saving?: boolean }
  ) => {
    setModalTitle(options?.title || null);
    setModalContent(content);
    setIsOpen(true);
    setSaving(options?.saving || false);
    setCanSubmit(true); // Reset canSubmit when opening modal
    setOnSubmitCallback(() => options?.onSubmit || null); // Store onSubmit callback
    setOnCancelCallback(() => options?.onCancel || null); // Store onCancel callback
  };

  const closeModal = () => {
    setModalContent(null);
    setIsOpen(false);
    setSaving(false); // Reset saving state
    setCanSubmit(true); // Reset canSubmit when closing modal
  };

  const handleCancel = async () => {
    if (onCancelCallback) {
      try {
        // Check if the callback returns a promise (async)
        const result = onCancelCallback();
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        console.error("Error executing onCancel callback:", error);
      }
    }
    closeModal();
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Execute the registered callback from useRef if it exists
      if (handleSubmitCallbackRef.current) {
        await handleSubmitCallbackRef.current();
      }

      // Execute the onSubmit callback if provided, making sure it's the last one executed
      if (onSubmitCallback) {
        const result = onSubmitCallback();
        if (result instanceof Promise) {
          await result;
        }
      }
    } catch (error) {
      console.error("Error executing submit callbacks:", error);
    } finally {
      setSaving(false);
    }
  };

  // Register the callback function using useRef
  const registerSubmitCallback = (callback: () => Promise<void>) => {
    handleSubmitCallbackRef.current = callback;
  };

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
        setSaving,
        saving,
        registerSubmitCallback,
        setModalTitle,
        canSubmit,
        setCanSubmit,
      }}
    >
      {children}
      <Modal open={isOpen} onClose={closeModal}>
        <ModalDialog
          sx={{
            minWidth: "600px",
            maxWidth: "800px",
            maxHeight: "80vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography level="h4">{modalTitle}</Typography>
          <Divider sx={{ my: 2 }} />
          {/* Modal Content */}
          <Box flex="1" sx={{ overflowY: "auto" }}>
            {modalContent}
          </Box>

          {/* Modal Buttons */}
          <Box
            sx={{
              py: 2,
              px: 3,
              borderTop: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"
                }`,
              paddingBottom: 0,
              paddingRight: "8px",
              display: "flex",
              justifyContent: "flex-end",
              position: "sticky",
              bottom: 0,
              zIndex: 1,
            }}
          >
            <Button onClick={handleCancel} variant="plain">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving || !canSubmit} sx={{ ml: 2 }}>
              {saving ? <CircularProgress size="sm" /> : "Save"}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </ModalContext.Provider>
  );
};
